// "Filter on anything" for a list (api/filters.ts): a "+ Filter" button that
// opens a dialog (a bottom sheet on a phone) to pick a field, an operator and a
// value; the active filters as removable chips, combined with AND; Clear all;
// and the result count. The fields, operators and choices come from the list's
// facets endpoint, so a new field on the server needs nothing here.

import { useMemo, useState } from 'react'
import { arity, describeFilter, OP_LABELS, type FacetField, type FilterOp, type ListFilter } from '@/api/filters'
import { Button } from './Button'
import { Dialog } from './Dialog'
import { FormField, SelectInput, TextInput } from './Field'
import { PlusIcon, XIcon } from './icons'

export interface FilterBarProps {
  /** "tenants", "signups": for the count and the labels. */
  noun: string
  fields: FacetField[] | undefined
  filters: ListFilter[]
  onChange: (next: ListFilter[]) => void
  /** The result count, once known. */
  total?: number
}

const today = () => new Date().toISOString().slice(0, 10)

function initialValues(field: FacetField | undefined, op: FilterOp): string[] {
  if (!field) return []
  const n = arity(op)
  if (n === 0) return []
  if (field.type === 'bool') return ['true']
  if (op === 'last_days') return ['30']
  if (field.type === 'date') return n === 2 ? [today(), today()] : [today()]
  if ((field.type === 'choice' || field.type === 'set') && op !== 'contains' && op !== 'any_of' && field.choices?.length) return [field.choices[0].value]
  return n === 2 ? ['', ''] : n === 'many' ? [] : ['']
}

function ValueInput({ field, op, values, onChange }: { field: FacetField; op: FilterOp; values: string[]; onChange: (v: string[]) => void }) {
  const n = arity(op)
  if (n === 0) return null
  const set = (i: number, v: string) => onChange(values.map((x, j) => (j === i ? v : x)))
  if (field.type === 'bool') {
    return (
      <FormField label="Value">
        {({ id }) => <SelectInput id={id} value={values[0] ?? 'true'} options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} onChange={(e) => onChange([e.target.value])} />}
      </FormField>
    )
  }
  if (op === 'last_days') {
    return (
      <FormField label="Days">
        {({ id }) => <TextInput id={id} type="number" min={1} max={3650} inputMode="numeric" value={values[0] ?? ''} onChange={(e) => onChange([e.target.value])} />}
      </FormField>
    )
  }
  const choices = field.choices ?? []
  if ((field.type === 'choice' || field.type === 'set') && op !== 'contains' && choices.length) {
    if (n === 'many') {
      return (
        <fieldset className="grid gap-1.5">
          <legend className="mb-1 text-xs font-semibold text-ink">Values</legend>
          <div className="grid max-h-60 gap-1 overflow-y-auto rounded-md border border-line p-2">
            {choices.map((c) => (
              <label key={c.value} className="flex min-h-[2rem] items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  checked={values.includes(c.value)}
                  onChange={(e) => onChange(e.target.checked ? [...values, c.value] : values.filter((v) => v !== c.value))}
                />
                <span className="min-w-0 break-words">{c.label}</span>
                {c.count !== undefined ? <span className="ml-auto text-xs text-subtle">{c.count}</span> : null}
              </label>
            ))}
          </div>
        </fieldset>
      )
    }
    return (
      <FormField label="Value">
        {({ id }) => (
          <SelectInput id={id} value={values[0] ?? ''} options={choices.map((c) => ({ value: c.value, label: c.count !== undefined ? `${c.label} (${c.count})` : c.label }))} onChange={(e) => onChange([e.target.value])} />
        )}
      </FormField>
    )
  }
  const type = field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'
  if (n === 2) {
    return (
      <div className="grid grid-cols-2 gap-2">
        <FormField label="From">{({ id }) => <TextInput id={id} type={type} value={values[0] ?? ''} onChange={(e) => set(0, e.target.value)} />}</FormField>
        <FormField label="To">{({ id }) => <TextInput id={id} type={type} value={values[1] ?? ''} onChange={(e) => set(1, e.target.value)} />}</FormField>
      </div>
    )
  }
  return (
    <FormField label="Value" hint={n === 'many' ? 'Separate values with commas.' : undefined}>
      {({ id }) => (
        <TextInput
          id={id}
          type={type}
          maxLength={200}
          value={n === 'many' ? values.join(', ') : values[0] ?? ''}
          onChange={(e) => onChange(n === 'many' ? e.target.value.split(',').map((v) => v.trim()).filter(Boolean) : [e.target.value])}
        />
      )}
    </FormField>
  )
}

function ready(field: FacetField | undefined, op: FilterOp, values: string[]): boolean {
  if (!field) return false
  const n = arity(op)
  if (n === 0) return true
  if (n === 'many') return values.length > 0
  if (n === 2) return values.length === 2 && values.every((v) => v.trim() !== '')
  return values.length === 1 && values[0].trim() !== ''
}

export function FilterBar({ noun, fields, filters, onChange, total }: FilterBarProps) {
  const [open, setOpen] = useState(false)
  const [key, setKey] = useState('')
  const [op, setOp] = useState<FilterOp>('is')
  const [values, setValues] = useState<string[]>([])
  const byKey = useMemo(() => new Map((fields ?? []).map((f) => [f.key, f])), [fields])
  const field = byKey.get(key)

  const pickField = (k: string) => {
    const f = byKey.get(k)
    const firstOp = f?.ops[0] ?? 'is'
    setKey(k)
    setOp(firstOp)
    setValues(initialValues(f, firstOp))
  }
  const pickOp = (o: FilterOp) => {
    setOp(o)
    setValues(initialValues(field, o))
  }
  const start = () => {
    pickField(fields?.[0]?.key ?? '')
    setOpen(true)
  }
  const add = () => {
    if (!field || !ready(field, op, values)) return
    onChange([...filters, { field: field.key, op, values: values.map((v) => v.trim()) }])
    setOpen(false)
  }

  const groups = useMemo(() => {
    const out = new Map<string, FacetField[]>()
    for (const f of fields ?? []) out.set(f.group, [...(out.get(f.group) ?? []), f])
    return [...out.entries()]
  }, [fields])

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label={`Filters on ${noun}`}>
        <Button size="sm" icon={<PlusIcon size={14} />} onClick={start} disabled={!fields?.length} aria-label="Add a filter">
          Filter
        </Button>
        {filters.map((f, i) => {
          const text = describeFilter(f, byKey.get(f.field))
          return (
            <span key={`${f.field}-${i}`} className="inline-flex max-w-full items-center gap-1 rounded-full border border-line-strong bg-raised py-0.5 pl-2.5 pr-1 text-xs text-ink">
              <span className="min-w-0 break-words">{text}</span>
              <button
                type="button"
                aria-label={`Remove filter: ${text}`}
                onClick={() => onChange(filters.filter((_, j) => j !== i))}
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted hover:bg-hover hover:text-ink"
              >
                <XIcon size={12} />
              </button>
            </span>
          )
        })}
        {filters.length ? (
          <Button size="sm" variant="ghost" onClick={() => onChange([])}>
            Clear all
          </Button>
        ) : null}
        {total !== undefined ? (
          <span className="ml-auto text-xs font-semibold text-muted" role="status" data-testid="result-count">
            {total} {total === 1 ? noun.replace(/s$/, '') : noun}
            {filters.length ? ' match' : ''}
          </span>
        ) : null}
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Add a filter"
        description={`Every filter must match: they're combined with AND.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!ready(field, op, values)} onClick={add}>
              Add filter
            </Button>
          </>
        }
      >
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            add()
          }}
        >
          <FormField label="Field">
            {({ id }) => (
              <SelectInput
                id={id}
                value={key}
                options={[]}
                groups={groups.map(([group, list]) => ({ label: group, options: list.map((f) => ({ value: f.key, label: f.label })) }))}
                onChange={(e) => pickField(e.target.value)}
              />
            )}
          </FormField>
          {field ? (
            <>
              <FormField label="Operator">
                {({ id }) => <SelectInput id={id} value={op} options={field.ops.map((o) => ({ value: o, label: OP_LABELS[o] }))} onChange={(e) => pickOp(e.target.value as FilterOp)} />}
              </FormField>
              <ValueInput field={field} op={op} values={values} onChange={setValues} />
            </>
          ) : null}
        </form>
      </Dialog>
    </div>
  )
}
